import { UISref, UISrefProps } from "@uirouter/react";
import { useTranslation } from "react-i18next";
import { ComponentProps, FunctionComponent, ReactNode } from "react";
import styled, { css } from "styled-components";
import { CenteredFlex, Cover, Flex, FullSize } from "../../components/layout";
import {
  Widget,
  WidgetProps,
  WIDGET_BORDER_RADIUS,
} from "../../components/widget";
import { colours } from "../../styles/colours";

const blurred = css<{ blur?: boolean }>`
  ${(props) => (props.blur ? "filter: blur(2px)" : "")};
`;

export const Title = styled.div<{ blur?: boolean }>`
  font-size: 0.8em;
  ${blurred};
`;

export const Wrapper = styled(Flex)<
  { center?: boolean } & ComponentProps<typeof Flex>
>`
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  ${(props) =>
    props.center ? "justify-content: center; align-items: center" : ""};
  ${blurred}
`;

export const Amount = styled.span`
  font-size: 3em;
`;

export const Container = styled(
  ({ isLink, ...props }: WidgetProps & { isLink?: boolean }) => (
    <Widget {...props} />
  )
)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  ${(props) => (props.isLink ? "cursor: pointer" : "")};
`;

const WidgetCover = styled.div`
  ${CenteredFlex}
  ${Cover};
  border-radius: ${WIDGET_BORDER_RADIUS};
  background: rgba(255, 255, 255, 0.8);
  color: ${colours.black};
  font-size: 1.2em;
`;

const OuterLink = styled.a`
  ${FullSize};
  display: flex;
`;

export type TextWidgetProps = {
  title?: string;
  center?: boolean;
  comingSoon?: boolean | ReactNode;
  children?: React.ReactNode;
  to?: UISrefProps["to"];
  href?: string;
} & React.ComponentProps<typeof Widget>;

export const TextWidget: FunctionComponent<TextWidgetProps> = (props) => {
  const {
    title,
    children,
    center,
    comingSoon,
    to,
    href,
    ...propsToPass
  } = props;

  const { t } = useTranslation("common");

  const inner = (
    <Container {...propsToPass} isLink={!!to || !!href}>
      {title !== undefined ? <Title blur={!!comingSoon}>{title}</Title> : null}
      <Wrapper blur={comingSoon} center={center}>
        {children}
      </Wrapper>
      {comingSoon ? (
        <WidgetCover>
          {comingSoon === true ? `${t("coming-soon")}...` : comingSoon}
        </WidgetCover>
      ) : null}
    </Container>
  );

  if (href) {
    return (
      <OuterLink href={href} rel="noreferrer" target="_blank">
        {inner}
      </OuterLink>
    );
  }
  if (to) {
    return <UISref to={to}>{inner}</UISref>;
  }
  return inner;
};
